import React, { useState, useEffect } from "react";
import { db } from "../../Firebase.js";
import { useHistory } from "react-router-dom";
import { StepSelector, Stepper } from "./stepper";
import { ConsentStep, CurrentProjectStep, NeedHelpWithStep, WriterExperienceStep, CanIReadItStep, GenreStep, NewProjectOrDemoStep } from "./steps";
import { NewSurveyPageWrapper, SurveyContainer } from "./styles";
import Auth from "../../Auth";
import { mutate } from "swr";
import useUserMetadata from "../../hooks/useUserMetadata";
import { Button } from "../Button";
import { sendAppEvent } from "../../Utils";
import { useAllFoldersStore } from "../AppHome/store";
import ShadowOfferModal from "./shadowOfferModal";

const noOp = () => {};

export const variationName = "conversational";

export default function NewSurveyTest({ userData }) {
  const history = useHistory();
  const { userMetadata } = useUserMetadata();
  const [surveyCompleted, setSurveyCompleted] = useState(false);
  const { data, update } = useAllFoldersStore();

  const { projectsById } = data(userData.userId);

  useEffect(() => {
    const surveyCompleted = !!userMetadata?.survey?.completedAt;
    setSurveyCompleted(surveyCompleted);
  }, [userMetadata]);

  const getUntitledProjectLink = () => {
    for (const projectId in projectsById) {
      const project = projectsById[projectId];
      if (project.title === "Untitled") {
        return `/app/project/${project.firebase_id}?sb=1`;
      }
    }
    return null;
  };

  useEffect(() => {
    // Call the function after a 5 second delay
    const timer = setTimeout(async () => {
      await update();
    }, 5000);

    // Clear the timeout if the component unmounts
    return () => clearTimeout(timer);
  }, [update]);

  const handleComplete = async (survey) => {
    await Auth.fetchWithIdToken("/api/user-metadata/survey", {
      survey,
    });
    await mutate("/api/user-metadata/get");
    let nextPage = "/app";
    if (survey.newProjectOrDemo && survey.newProjectOrDemo === "PROJECT") {
      const untitledProjectLink = getUntitledProjectLink();
      if (untitledProjectLink) {
        nextPage = untitledProjectLink;
      }
      userData.update({ needsDemo: false });
    }
    history.push(nextPage);
  };

  const completedBanner = (
    <div className="survey-completed">
      <p>You have already completed the survey</p>
      <Button onClick={() => history.replace("/app")}>Back to home page</Button>
    </div>
  );

  return <NewSurveyPageWrapper>{surveyCompleted ? completedBanner : <Survey onComplete={handleComplete} />}</NewSurveyPageWrapper>;
}

export function Survey({ onComplete = noOp, showConsent = false }) {
  const totalSteps = 5;
  const [step5, setStep5] = useState("import");

  const [selectedStep, setSelectedStep] = React.useState(showConsent ? 0 : 1);
  const [values, setValues] = useState({});
  const [actionsDisabled, setActionsDisabled] = useState(false);

  const [docId, setDocId] = useState(null);
  const [interviewLink, setInterviewLink] = useState("");
  const [loadedInterviewLink, setLoadedIntervewLink] = useState(false);
  const [isShadowModalOpen, setIsShadowModalOpen] = useState(false);

  // Track whether the user is a professional/aspiring writer for research opportunity
  const [isProfessionalWriter, setIsProfessionalWriter] = useState(false);

  useEffect(() => {
    const unsub = db
      .collection("newUserInterviews")
      .limit(1)
      .onSnapshot((snap) => {
        if (!snap.docs.length) {
          return;
        }
        const doc = snap.docs[0];
        const link = doc.get("newInterviewLink");
        setInterviewLink(link || "");
        setDocId(doc.id);
        setLoadedIntervewLink(true);
      });
    return unsub;
  }, []);

  function handleInterviewLink(join) {
    if (join) {
      if (docId) {
        db.collection("newUserInterviews").doc(docId).update({ newInterviewLink: "" });
      }
      if (interviewLink) {
        window.open(interviewLink, "_blank");
      }
      sendAppEvent("new_user_shadow_joined");
    } else {
      sendAppEvent("new_user_shadow_skipped");
    }
    setIsShadowModalOpen(false);

    // Continue with the survey - move to next step
    setSelectedStep((selectedStep) => selectedStep + 1);
  }

  const submitData = async (survey) => {
    setActionsDisabled(true);
    onComplete(survey);
  };

  const handleSkip = async () => {
    if (selectedStep === totalSteps) {
      await submitData(values);
    } else {
      setSelectedStep((selectedStep) => selectedStep + 1);
    }
  };

  const handleSubmit = async (stepName, value) => {
    const newValues = {
      ...values,
      [stepName]: value,
    };

    setValues(newValues);

    // Handle professional or aspiring professional writers
    if (stepName === "writerExperience") {
      const isProfessional = value === "PROFESSIONAL" || value === "ASPIRING_PROFESSIONAL";
      setIsProfessionalWriter(isProfessional);

      if (isProfessional && interviewLink) {
        // Show the research opportunity modal and stop here
        sendAppEvent("new_user_shadow_viewed");
        setIsShadowModalOpen(true);
        return; // Stop processing - don't advance to next step yet
      }
    }

    // Continue with normal survey flow
    if (selectedStep === totalSteps) {
      await submitData(newValues);
    } else {
      setSelectedStep((selectedStep) => selectedStep + 1);
    }

    sendAppEvent("survey_step_submitted", {
      step_name: stepName,
      step_number: selectedStep,
      survey_variation: variationName,
      step_value: value,
    });
  };

  const commonStepProps = {
    onSubmit: handleSubmit,
    onSkip: handleSkip,
    actionsDisabled,
  };

  const handleConsentAccept = () => {
    setSelectedStep(1);
  };

  const handleConsentReject = () => {
    onComplete();
  };

  return (
    <SurveyContainer>
      <Stepper
        step={selectedStep}
        totalSteps={totalSteps}
      >
        <StepSelector
          selected={selectedStep}
          steps={[
            {
              id: 0,
              content: (
                <ConsentStep
                  onAccept={handleConsentAccept}
                  onReject={handleConsentReject}
                />
              ),
            },
            {
              id: 1,
              content: (
                <WriterExperienceStep
                  name="writerExperience"
                  {...commonStepProps}
                />
              ),
            },
            {
              id: 2,
              content: (
                <NeedHelpWithStep
                  name="needHelpWith"
                  {...commonStepProps}
                />
              ),
            },
            {
              id: 3,
              content: (
                <CurrentProjectStep
                  name="currentProject"
                  {...commonStepProps}
                />
              ),
            },
            {
              id: 4,
              content: (
                <GenreStep
                  name="genre"
                  {...commonStepProps}
                />
              ),
            },
            {
              id: 5,
              content:
                step5 === "import" ? (
                  <CanIReadItStep
                    name="canIReadIt"
                    {...commonStepProps}
                  />
                ) : (
                  <NewProjectOrDemoStep
                    name="newProjectOrDemo"
                    {...commonStepProps}
                  />
                ),
            },
          ]}
        />
      </Stepper>
      {isShadowModalOpen && (
        <ShadowOfferModal
          isOpen={isShadowModalOpen}
          onClose={() => {
            setIsShadowModalOpen(false);
            setSelectedStep((selectedStep) => selectedStep + 1);
          }}
          onButtonClick={handleInterviewLink}
        />
      )}
    </SurveyContainer>
  );
}
